import { Book } from "../types/book";
import { cleanAndFormatTextWithClaude } from "./claudeFormat";
import {
  Room,
  RoomEvent,
  DataPacket_Kind,
  AudioTrack,
  RemoteAudioTrack,
  Track,
} from "livekit-client";

export interface SpeechConfig {
  voice?: string;
  rate?: number;
  pitch?: number;
  volume?: number;
}

interface XAIVoice {
  id: string;
  name: string;
  language: string;
}

export class TextToSpeechService {
  private room: Room; // Now passed in constructor
  private audioTrack: AudioTrack | null = null;
  private contentQueue: string[] = [];
  private utteranceQueue: { text: string; audio_id: string }[] = [];
  private isPlaying = false;
  private currentIndex = 0;
  private maxPreload = 3;
  private onProgressUpdate?: (progress: number) => void;
  private onWordBoundary?: (charIndex: number, word: string) => void;
  public lastCharIndex: number | null = null;
  public lastSectionIndex: number | null = null;
  private currentBookId: string | null = null;
  private wordTimings: { charIndex: number; time: number }[] = [];
  private currentAudioStartTime: number = 0;
  private progressHandlers: ((progress: number) => void)[] = [];

  constructor(room: Room) {
    this.room = room;
    this.adjustForBrowser();
    this.initializeRoom();
  }

  private adjustForBrowser(): void {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      this.maxPreload = 2;
    }
  }

  private initializeRoom() {
    if (this.room.state !== "connected") {
      console.warn("[TTS] Room not connected during initialization");
      return;
    }

    try {
      // Handle incoming data from TTS service
      this.room.on(
        RoomEvent.DataReceived,
        (payload: Uint8Array, participant, kind) => {
          if (kind === DataPacket_Kind.RELIABLE && participant) {
            try {
              const decoder = new TextDecoder();
              const data = JSON.parse(decoder.decode(payload));
              console.log("[TTS] Received metadata:", data);

              if (data.action === "speak" && data.section_index !== undefined) {
                this.currentIndex = data.section_index;
                this.updateProgress(
                  this.currentIndex / this.contentQueue.length
                );
              } else if (data.action === "section_update") {
                this.currentIndex = data.section_index;
                this.updateProgress(data.progress);
              } else if (data.action === "paused") {
                this.isPlaying = false;
                // Notify React component about pause
                this.updateProgress(
                  this.currentIndex / this.contentQueue.length
                );
              } else if (data.action === "word_boundary") {
                console.log("[TTS] Word boundary received:", data);
                if (this.onWordBoundary) {
                  this.onWordBoundary(data.charIndex, data.word);
                }
              }
            } catch (error) {
              console.error("[TTS] Error processing received data:", error);
            }
          }
        }
      );

      // Handle audio track subscriptions
      this.room.on(
        RoomEvent.TrackSubscribed,
        (track, publication, participant) => {
          if (track.kind === Track.Kind.Audio) {
            console.log(
              "[TTS] Audio track subscribed:",
              track.sid,
              "source:",
              track.source
            );
            this.audioTrack = track as AudioTrack;

            // Attach to audio element for playback
            const audioElement = document.createElement("audio");
            audioElement.autoplay = true;
            audioElement.volume = 1.0;
            track.attach(audioElement);

            // Add to DOM to ensure playback
            audioElement.style.display = "none";
            document.body.appendChild(audioElement);

            console.log("[TTS] Audio element created and attached");
          }
        }
      );

      // Handle track unsubscribed
      this.room.on(
        RoomEvent.TrackUnsubscribed,
        (track, publication, participant) => {
          if (track === this.audioTrack) {
            console.log("[TTS] Audio track unsubscribed:", track.sid);
            const audioElements = track.detach();
            // Remove audio elements from DOM
            audioElements.forEach((el) => {
              if (el.parentNode) {
                el.parentNode.removeChild(el);
              }
            });
            this.audioTrack = null;
          }
        }
      );

      console.log("[TTS] LiveKit room initialized with audio handling");
    } catch (error) {
      console.error("[TTS] Error initializing room:", error);
    }
  }

  public setProgressCallback(callback: (progress: number) => void) {
    this.onProgressUpdate = callback;
  }

  public setWordBoundaryCallback(
    callback: (charIndex: number, word: string) => void
  ) {
    this.onWordBoundary = (charIndex, word) => {
      this.lastCharIndex = charIndex;
      this.lastSectionIndex = this.currentIndex;
      callback(charIndex, word);
    };
  }

  public async loadContent(book: Book): Promise<void> {
    if (!book.fileUrl) throw new Error("No file URL provided");
    this.currentBookId = book.id;
    const cacheKey = `cleanedText_${book.id}`;
    let content = localStorage.getItem(cacheKey);

    if (!content) {
      console.log(
        `[TTS] No cache found for book ${book.id}, extracting and cleaning...`
      );
      content = "";
      if (book.fileType === "pdf") {
        const { getDocument } = await import("pdfjs-dist");
        const loadingTask = getDocument(book.fileUrl);
        const pdf = await loadingTask.promise;
        const pages: string[] = [];
        const cleanedPages: string[] = [];
        const batchSize = 2;

        for (
          let batchStart = 1;
          batchStart <= pdf.numPages;
          batchStart += batchSize
        ) {
          const batchEnd = Math.min(batchStart + batchSize - 1, pdf.numPages);
          const pagePromises = [];

          for (let i = batchStart; i <= batchEnd; i++) {
            pagePromises.push(
              pdf.getPage(i).then(async (page: any) => {
                const textContent = await page.getTextContent();
                return textContent.items.map((item: any) => item.str).join(" ");
              })
            );
          }

          const pageTexts = await Promise.all(pagePromises);
          for (let idx = 0; idx < pageTexts.length; idx++) {
            pages.push(pageTexts[idx]);
            const cleaned = await cleanAndFormatTextWithClaude(
              pageTexts[idx],
              "gemini"
            );
            cleanedPages.push(cleaned);
            if (this.onProgressUpdate) {
              const pageNum = batchStart + idx;
              this.onProgressUpdate(
                Math.min(10 + (pageNum / pdf.numPages) * 80, 90)
              );
            }
            await new Promise((r) => setTimeout(r, 1000));
          }
        }
        content = cleanedPages.join("\n\n");
      } else if (book.fileType === "txt") {
        const response = await fetch(book.fileUrl);
        content = await response.text();
        if (this.onProgressUpdate) {
          this.onProgressUpdate(30);
        }
      } else {
        throw new Error(`Unsupported file type: ${book.fileType}`);
      }

      if (this.onProgressUpdate) {
        this.onProgressUpdate(80);
      }

      content = await cleanAndFormatTextWithClaude(content, "gemini");

      if (this.onProgressUpdate) {
        this.onProgressUpdate(90);
      }

      if (content) {
        try {
          localStorage.setItem(cacheKey, content);
          console.log(`[TTS] Cached cleaned content for book ${book.id}`);
        } catch (e) {
          console.warn(
            "[TTS] Failed to cache content due to storage limits:",
            e
          );
        }
        this.contentQueue = this.splitContent(content);
        if (this.contentQueue.length === 0) {
          throw new Error("No readable content was found in this book.");
        }
        // Send content to server
        console.log("[TTS] Sending load_content to server:", {
          bookId: book.id,
          contentLength: this.contentQueue.length,
          firstChunk: this.contentQueue[0]?.substring(0, 50) + "...",
        });
        await this.notifyServer("load_content", {
          bookId: book.id,
          content: this.contentQueue,
        });
        this.currentIndex = 0;
        if (this.onProgressUpdate) {
          this.onProgressUpdate(100);
        }
      } else {
        throw new Error("Failed to load or process content");
      }
    } else {
      console.log(
        `[TTS] Loaded cleaned content for book ${book.id} from cache.`
      );
      this.contentQueue = this.splitContent(content);
      if (this.contentQueue.length === 0) {
        throw new Error("No readable content was found in this book.");
      }
      // Send content to server
      console.log("[TTS] Sending cached load_content to server:", {
        bookId: book.id,
        contentLength: this.contentQueue.length,
        firstChunk: this.contentQueue[0]?.substring(0, 50) + "...",
      });
      await this.notifyServer("load_content", {
        bookId: book.id,
        content: this.contentQueue,
      });
      this.currentIndex = 0;
      if (this.onProgressUpdate) {
        this.onProgressUpdate(100);
      }
    }
  }

  private splitContent(content: string): string[] {
    const cleanContent = content
      .replace(/\n---PAGE BREAK---\n/g, "\n\n")
      .replace(/\s+/g, " ")
      .trim();
    const sentenceDelimiter = /(?<=[.!?])\s+/;
    const sentences = cleanContent
      .split(sentenceDelimiter)
      .filter((s) => s.trim().length > 0);
    const chunks: string[] = [];
    let currentChunk = "";
    const maxChunkSize = 500;

    for (const sentence of sentences) {
      const potentialChunk = currentChunk
        ? `${currentChunk} ${sentence}`
        : sentence;
      if (potentialChunk.length > maxChunkSize && currentChunk) {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk = potentialChunk;
      }
    }
    if (currentChunk) chunks.push(currentChunk.trim());

    if (chunks.length === 0 && cleanContent.length > 0) {
      console.warn("[TTS] Fallback: returning whole content as single chunk");
      return [cleanContent];
    }

    console.log("[TTS] Generated chunks:", chunks);
    return chunks.filter(Boolean);
  }

  public async speak(config: SpeechConfig = {}, startIndex?: number) {
    if (!this.contentQueue.length) {
      console.error("[TTS] No content loaded");
      throw new Error("No content loaded");
    }

    // Ensure content is loaded on server before speaking
    if (this.currentBookId) {
      console.log("[TTS] Ensuring content is loaded on server before speaking");
      await this.notifyServer("load_content", {
        bookId: this.currentBookId,
        content: this.contentQueue,
      });
    }

    if (typeof startIndex === "number") {
      this.currentIndex = startIndex;
    }
    this.isPlaying = true;
    console.log("[TTS] speak called:", {
      voice: config.voice || "Aria",
      startIndex: this.currentIndex,
      text: this.contentQueue[this.currentIndex].slice(0, 50) + "...",
    });
    await this.notifyServer("speak", {
      voice: config.voice || "Aria",
      startIndex: this.currentIndex,
    });
  }

  private async notifyServer(action: string, payload: any) {
    try {
      if (this.room.state !== "connected") {
        console.warn("[TTS] Room not connected, skipping data publish");
        return;
      }
      const message = { action, ...payload };
      console.log(
        "[TTS] Room participants:",
        this.room.remoteParticipants.size
      );
      console.log("[TTS] Sending message to server:", message);
      await this.room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify(message)),
        { reliable: true, topic: "tts_metadata" }
      );
      console.log("[TTS] Message sent successfully");
    } catch (error) {
      console.error("[TTS] Failed to publish data:", error);
    }
  }

  public pause() {
    this.isPlaying = false;
    this.notifyServer("pause", {});
    // Update progress handlers immediately
    this.updateProgress(this.currentIndex / this.contentQueue.length);
  }

  public async resume(config: SpeechConfig = {}) {
    if (!this.isPlaying && this.contentQueue[this.currentIndex]) {
      this.isPlaying = true;
      await this.notifyServer("resume", { voice: config.voice || "Aria" });
    }
  }

  public stop() {
    this.isPlaying = false;
    this.currentIndex = 0;
    this.utteranceQueue = [];
    this.wordTimings = [];
    this.notifyServer("stop", {});
  }

  public seekTo(index: number, config: SpeechConfig = {}) {
    if (index >= 0 && index < this.contentQueue.length) {
      this.stop();
      this.currentIndex = index;
      this.notifyServer("seek", { index, voice: config.voice || "Aria" });
    }
  }

  public async getAvailableVoices(): Promise<XAIVoice[]> {
    return [
      {
        id: "AZnzlk1XvdvUeBnXmlld",
        name: "Domi (Warm ASMR Female)",
        language: "en",
      },
      {
        id: "EXAVITQu4vr4xnSDxMaL",
        name: "Bella (Soft Whisper Female)",
        language: "en",
      },
      {
        id: "pNInz6obpgDQGcFmaJgB",
        name: "Adam (Gentle Male)",
        language: "en",
      },
      {
        id: "21m00Tcm4TlvDq8ikWAM",
        name: "Rachel (Calm Female)",
        language: "en",
      },
      {
        id: "CYw3kZ02Hs0563khs1Fj",
        name: "Dave (Soothing Male)",
        language: "en",
      },
    ];
  }

  public isCurrentlyPlaying(): boolean {
    return this.isPlaying;
  }

  public getCurrentIndex(): number {
    return this.currentIndex;
  }

  public getTotalSections(): number {
    return this.contentQueue.length;
  }

  public static removeCachedContent(bookId: string) {
    const cacheKey = `cleanedText_${bookId}`;
    localStorage.removeItem(cacheKey);
  }

  public clearContent() {
    this.contentQueue = [];
    this.currentIndex = 0;
    this.lastCharIndex = null;
    this.lastSectionIndex = null;
    this.isPlaying = false;
    this.utteranceQueue = [];
    this.wordTimings = [];
    this.notifyServer("clear", {});
  }

  public async speakFromCharIndex(
    sectionIndex: number,
    charIndex: number,
    config: SpeechConfig = {}
  ) {
    if (!this.contentQueue[sectionIndex] || charIndex < 0) {
      console.error("[TTS] Invalid section or char index:", {
        sectionIndex,
        charIndex,
        queueLength: this.contentQueue.length,
      });
      return;
    }
    const section = this.contentQueue[sectionIndex];
    const textToSpeak = section.slice(charIndex).trim();
    if (!textToSpeak) {
      console.error("[TTS] No text to speak at index:", sectionIndex);
      return;
    }

    this.stop();
    this.currentIndex = sectionIndex;
    this.isPlaying = true;
    console.log("[TTS] speakFromCharIndex called:", {
      sectionIndex,
      charIndex,
      voice: config.voice || "Aria",
      text: textToSpeak.slice(0, 50) + "...",
    });
    await this.notifyServer("speak_from_char", {
      sectionIndex,
      charIndex,
      voice: config.voice || "Aria",
    });
    this.lastCharIndex = charIndex;
    this.lastSectionIndex = sectionIndex;
  }

  public getContentQueue(): string[] {
    return this.contentQueue;
  }

  public getCurrentBookId(): string | null {
    return this.currentBookId;
  }

  public setAudioTrack(track: AudioTrack) {
    this.audioTrack = track;
  }

  // Progress handling
  public onProgress(callback: (progress: number) => void): void {
    this.progressHandlers.push(callback);
  }

  public offProgress(): void {
    this.progressHandlers = [];
  }

  private updateProgress(progress: number): void {
    this.progressHandlers.forEach((handler) => handler(progress));
    // Also update internal progress for UI
    if (this.onProgressUpdate) {
      this.onProgressUpdate(progress);
    }
  }

  // Playback control methods
  public play(): void {
    if (this.contentQueue.length > 0 && !this.isPlaying) {
      this.isPlaying = true;
      this.resume();
    }
  }

  public cleanup(): void {
    this.stop();
    this.offProgress();
    this.audioTrack?.stop();
    this.audioTrack = null;
  }

  public getCurrentSection(): string {
    return this.contentQueue[this.currentIndex] || "";
  }

  public setVoice(voice: XAIVoice): void {
    // Implementation depends on your TTS service
    console.log("Voice set to:", voice);
  }
}

export default function createTtsService(room: Room) {
  return new TextToSpeechService(room);
}
